import React from "react";
import PropTypes from "prop-types";
import { get } from "lodash";
import Warning from "MODULES/shared/components/Warning/Warning";
import { dollars } from "LIB/formatters/currency";
import {
  REPAIR_AND_HOLD,
  CUSTOMER_RMA_REQUESTED,
  REPAIR_EVALUATION_APPROVED,
  QC_HOLD,
  UNDER_REVIEW
} from "MODULES/inventories/constants";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { StatusTag } from "MODULES/shared/components";

export function getLineItemAndCost(inventory) {
  const lineItem = get(inventory, "repair_order_line_items[0]");
  if (!lineItem) return [null, null];
  if (lineItem.exchange) return [null, null];
  const totalCost = dollars(
    parseFloat(lineItem.labor_cost || 0) +
      parseFloat(lineItem.outside_labor_cost || 0) +
      parseFloat(lineItem.part_cost || 0)
  );

  return [lineItem, totalCost];
}

export const costWarningText = cost =>
  `Warning: Cost does not yet reflect repair expense. Estimated Total Repair Cost: ${cost}.`;

export function CostWarning({ inventory }) {
  const [lineItem, totalCost] = getLineItemAndCost(inventory);

  if (!lineItem) return null;

  const showWarning =
    lineItem.approval_status === REPAIR_EVALUATION_APPROVED &&
    !lineItem.exchange &&
    lineItem.original_evaluation &&
    !lineItem.final_evaluation_approved;

  return (
    showWarning && (
      <table className="table table-condensed">
        <tbody>
          <tr>
            <Warning>{costWarningText(totalCost)}</Warning>
          </tr>
        </tbody>
      </table>
    )
  );
}

CostWarning.propTypes = {
  inventory: PropTypes.shape({
    repair_order_line_item: PropTypes.shape({
      labor_cost: PropTypes.number.isRequired,
      outside_labor_cost: PropTypes.number.isRequired,
      part_cost: PropTypes.number.isRequired
    }).isRequired
  }).isRequired
};

export const RepairAndHold = ({ inventory, label = "Repair & Hold" }) => {
  if (REPAIR_AND_HOLD !== inventory.repair_status) {
    return null;
  }

  const RepairAndHoldBadge = (
    <span className="label label-warning">
      <i className="fa fa-wrench" />
      &nbsp;&nbsp;{label}
    </span>
  );

  const [lineItem, totalCost] = getLineItemAndCost(inventory);

  if (!lineItem) {
    return RepairAndHoldBadge;
  }

  const tooltip = (
    <Tooltip id={inventory.id}>{costWarningText(totalCost)}</Tooltip>
  );

  return (
    <OverlayTrigger placement="right" overlay={tooltip} animation>
      {RepairAndHoldBadge}
    </OverlayTrigger>
  );
};

RepairAndHold.propTypes = {
  inventory: PropTypes.shape({}),
  label: PropTypes.string
};

export default function Warnings({ inventory }) {
  const hasCreditIssued = get(
    inventory,
    "most_recent_customer_rma.credit_issued"
  );

  const hasCreditDenied = get(
    inventory,
    "most_recent_customer_rma.credit_denied"
  );

  const hasWarrantyConsideration = get(
    inventory,
    "most_recent_customer_rma.warranty_consideration"
  );

  const hasCreditRequested = get(
    inventory,
    "most_recent_customer_rma.credit_requested"
  );

  const hasWarrantyStatus = get(
    inventory,
    "most_recent_customer_rma.warranty_status"
  );

  const activeRma =
    (hasCreditRequested || hasWarrantyConsideration) &&
    !hasCreditDenied &&
    !hasCreditIssued &&
    !hasWarrantyStatus;

  const discrepancies = !inventory.discrepancies_resolved && (
    <span className="label label-danger">
      <i className="fa fa-warning" />
      &nbsp;&nbsp;Disc. Reported
    </span>
  );

  const customerRMA = activeRma &&
    inventory.status !== CUSTOMER_RMA_REQUESTED && (
      <span className="label label-danger">
        <i className="fa fa-cog" />
        &nbsp;&nbsp;Cust. RMA
      </span>
    );

  const qcHold = inventory.qc_status === QC_HOLD && (
    <span className="label label-danger">
      <i className="fa fa-pause" />
      &nbsp;&nbsp;QC Hold
    </span>
  );

  const rmaRequested = inventory.status === CUSTOMER_RMA_REQUESTED &&
    !hasCreditIssued && (
      <span className="label label-danger">
        <i className="fa fa-cog" />
        &nbsp;&nbsp;Cust. RMA Requested
      </span>
    );

  const underReview = inventory.qc_status === UNDER_REVIEW && (
    <span className="label label-warning">
      <i className="fa fa-pause" />
      &nbsp;&nbsp;Under Review
    </span>
  );

  const inTransfer = inventory.active_transfer && (
    <span className="label label-warning">
      <i className="fa fa-plane" />
      &nbsp;&nbsp;In Transfer
    </span>
  );

  const doNotExchange = (inventory.do_not_exchange ||
    (inventory.part && inventory.part.pass_fail) ||
    inventory.do_not_exchange_due_to_outright_value) &&
    inventory.condition !== "as_removed" && (
      <span className="label label-danger">
        <i className="fa fa-times-circle-o" />
        &nbsp;&nbsp;DO NOT EXCHANGE
      </span>
    );

  const doNotSell = inventory.unsellable_per_user && (
    <span className="label label-warning">
      <i className="fa fa-diamond" />
      &nbsp;&nbsp;DO NOT SELL!
    </span>
  );

  const reclaimed = inventory.reclaimed && (
    <span className="label label-warning">
      <i className="fa fa-superpowers" />
      &nbsp;&nbsp;RECLAIMED
    </span>
  );

  const outright = inventory.exchange_gone_outright && (
    <span className="label label-warning">&nbsp;EXCHANGE GONE OUTRIGHT</span>
  );

  const consignment = (inventory.consignment_type ||
    inventory.consigned_date) && (
    <StatusTag
      key="consignment"
      display="warning"
      title={
        inventory.consignment_type
          ? inventory.consignment_type.toUpperCase()
          : "CONSIGNMENT"
      }
      icon="location-arrow"
    />
  );

  const coreReturn = inventory.returned_core_for_exchange_id && (
    <span className="label label-success" style={{ marginRight: "10px" }}>
      <i className="fa fa-reply" />
      &nbsp;&nbsp;Core Return
    </span>
  );

  const consignmentCancelled = get(
    inventory,
    "consignment_details.cancelled"
  ) && <StatusTag icon="ban" title="Consignment Cancelled" />;

  const managedRepair = (inventory.managed_repair_repaired ||
    inventory.managed_repair_in_progress) && (
    <StatusTag icon="recycle" title="Managed Repair" display="success" />
  );

  const internalOutright = get(inventory, "sales_order_line_items", []).some(
    item => item.internal_outright
  ) && <StatusTag icon="check" title="Internal Outright" display="success" />;

  return (
    <span>
      {internalOutright}
      {customerRMA}
      {coreReturn}
      {consignment}
      {consignmentCancelled}
      {discrepancies}
      {doNotExchange}
      {doNotSell}
      {outright}
      {qcHold}
      {reclaimed}
      {rmaRequested}
      {underReview}
      {inTransfer}
      <RepairAndHold inventory={inventory} />
      {managedRepair}
    </span>
  );
}

Warnings.propTypes = {
  inventory: PropTypes.shape({
    qc_status: PropTypes.string,
    discrepancies_resolved: PropTypes.bool.isRequired
  })
};



// WEBPACK FOOTER //
// ./app/modules/inventories/components/Detail/Warnings.jsx